import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Upload,
  Trash2,
  CheckCircle2,
  Package,
  Calendar,
  User,
  HardDrive,
  Star,
  AlertCircle,
  Link2,
  Copy,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { authHeaders, formatApiErrorDetail } from "@/lib/auth";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function BetaAPKPanel() {
  const [apks, setApks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [setActive, setSetActive] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  const activeApk = useMemo(() => apks.find((a) => a.is_active), [apks]);
  const permanentLink = useMemo(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/apk/latest`;
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(permanentLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy link. Please copy manually.");
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/apks", { headers: authHeaders() });
      setApks(data || []);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(""), 3500);
    return () => clearTimeout(t);
  }, [success]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please select an APK file.");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".apk")) {
      setError("Only .apk files are allowed.");
      return;
    }
    if (!version.trim()) {
      setError("Version is required (e.g. v0.1.0-beta).");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("version", version.trim());
      form.append("notes", notes.trim());
      form.append("set_active", setActive ? "true" : "false");
      form.append("file", file);
      await api.post("/admin/apks", form, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      setSuccess(`Uploaded ${version}`);
      setVersion("");
      setNotes("");
      setFile(null);
      setProgress(0);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const activate = async (apk) => {
    setError("");
    try {
      await api.post(
        `/admin/apks/${apk.id}/activate`,
        {},
        { headers: authHeaders() },
      );
      setSuccess(`Activated ${apk.version}`);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  const remove = async (apk) => {
    if (!window.confirm(`Delete APK ${apk.version}? This file will be permanently removed.`))
      return;
    setError("");
    try {
      await api.delete(`/admin/apks/${apk.id}`, { headers: authHeaders() });
      setSuccess(`Deleted ${apk.version}`);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <section data-testid="apk-panel" className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold text-[#0E2A36]">Beta APK builds</h2>
        <p className="text-sm text-[#3D5560]">
          Upload your APK once and approve users from the Beta Signups tab. The active
          build is what users get when you click "Approve & send APK".
        </p>
      </div>

      {/* Permanent public link to the currently active APK */}
      <div
        data-testid="apk-permanent-link-card"
        className="bg-gradient-to-br from-[#0E2A36] to-[#1F4E5F] rounded-3xl p-5 sm:p-6 text-[#F4FAF8] shadow-[0_8px_30px_rgba(15,56,32,0.12)]"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-[#F4FAF8]/10 border border-[#F4FAF8]/20 flex items-center justify-center">
            <Link2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-bold">Permanent download link</h3>
            <p className="text-xs text-[#F4FAF8]/70 mt-0.5">
              Always serves the currently <strong>active</strong> APK. Share this
              URL anywhere — it never changes when you upload a new build.
            </p>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="apk-permanent-link-input"
                type="text"
                readOnly
                value={permanentLink}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 px-3 py-2 rounded-xl bg-[#F4FAF8]/10 border border-[#F4FAF8]/20 text-sm text-[#F4FAF8] font-mono focus:outline-none focus:ring-2 focus:ring-[#F4FAF8]/30"
              />
              <div className="flex gap-2">
                <button
                  data-testid="apk-permanent-link-copy"
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center justify-center gap-1.5 bg-[#F4FAF8] text-[#0E2A36] hover:bg-[#E1EEEA] px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap"
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy link
                    </>
                  )}
                </button>
                <a
                  data-testid="apk-permanent-link-open"
                  href={permanentLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 border border-[#F4FAF8]/30 hover:bg-[#F4FAF8]/10 text-[#F4FAF8] px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
              </div>
            </div>
            <p
              data-testid="apk-permanent-link-status"
              className="mt-3 text-xs text-[#F4FAF8]/80"
            >
              {activeApk ? (
                <>
                  Currently serving:{" "}
                  <span className="font-semibold text-[#F4FAF8]">
                    {activeApk.version}
                  </span>{" "}
                  <span className="text-[#F4FAF8]/60">({activeApk.filename})</span>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-200">
                  <AlertCircle className="w-3.5 h-3.5" />
                  No active build yet — upload an APK below and mark it active.
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Upload form */}
      <form
        data-testid="apk-upload-form"
        onSubmit={handleUpload}
        className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_4px_20px_rgba(15,56,32,0.04)] p-5 sm:p-6"
      >
        <h3 className="font-display text-lg font-bold text-[#0E2A36] mb-4">
          Upload new build
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
              Version label *
            </label>
            <input
              data-testid="apk-version-input"
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v0.1.0-beta"
              className="w-full px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
              APK file *
            </label>
            <input
              data-testid="apk-file-input"
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-[#3D5560] file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#1F4E5F] file:text-[#F4FAF8] file:font-semibold file:cursor-pointer"
            />
            {file && (
              <p className="mt-1.5 text-xs text-[#3D5560]">
                {file.name} · {fmtSize(file.size)}
              </p>
            )}
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#1F4E5F] mb-1.5">
              Release notes (optional)
            </label>
            <textarea
              data-testid="apk-notes-input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed in this build?"
              className="w-full px-4 py-2.5 rounded-xl bg-[#F4FAF8] border border-[#C9DDD6] focus:outline-none focus:ring-2 focus:ring-[#1F4E5F]/30 text-sm"
            />
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <input
              id="apk-active"
              data-testid="apk-active-checkbox"
              type="checkbox"
              checked={setActive}
              onChange={(e) => setSetActive(e.target.checked)}
              className="w-4 h-4 rounded border-[#C9DDD6] text-[#1F4E5F] focus:ring-[#1F4E5F]/40"
            />
            <label htmlFor="apk-active" className="text-sm text-[#0E2A36]">
              Set as <strong>active build</strong> (used by new approvals)
            </label>
          </div>
        </div>
        {uploading && progress > 0 && (
          <div className="mt-4">
            <div className="h-2 bg-[#E1EEEA] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1F4E5F] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-[#3D5560] mt-1.5">Uploading… {progress}%</p>
          </div>
        )}
        <div className="mt-5">
          <button
            data-testid="apk-upload-submit"
            type="submit"
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-[#1F4E5F] hover:bg-[#173E50] disabled:opacity-60 text-[#F4FAF8] px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload APK
          </button>
        </div>
      </form>

      {/* Messages */}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {success && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {success}
        </div>
      )}

      {/* Builds list */}
      <div className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_4px_20px_rgba(15,56,32,0.04)] overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-[#E1EEEA] bg-[#F4FAF8]/60 flex items-center justify-between">
          <h3 className="font-display font-bold text-[#1F4E5F]">All builds</h3>
          <span className="text-xs text-[#3D5560]/80">
            {apks.length} {apks.length === 1 ? "build" : "builds"}
          </span>
        </div>
        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#3D5560]">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading builds…
          </div>
        ) : apks.length === 0 ? (
          <div className="px-6 py-12 text-center text-[#3D5560]">
            <Package className="w-8 h-8 mx-auto mb-3 text-[#3D5560]/60" />
            No APK builds uploaded yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#E1EEEA]">
            {apks.map((apk) => (
              <li
                key={apk.id}
                data-testid={`apk-row-${apk.id}`}
                className="px-5 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Package className="w-4 h-4 text-[#1F4E5F]" />
                    <p className="font-semibold text-[#0E2A36]">{apk.version}</p>
                    {apk.is_active && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Star className="w-3 h-3" /> Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#3D5560] mt-1 truncate">{apk.filename}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#3D5560]">
                    <span className="inline-flex items-center gap-1">
                      <HardDrive className="w-3 h-3" /> {fmtSize(apk.size_bytes)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {fmtDate(apk.uploaded_at)}
                    </span>
                    {apk.uploaded_by && (
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" /> {apk.uploaded_by}
                      </span>
                    )}
                  </div>
                  {apk.notes && (
                    <p className="mt-2 text-[13px] text-[#0E2A36] bg-[#F4FAF8] border border-[#C9DDD6] rounded-xl px-3 py-2 whitespace-pre-line">
                      {apk.notes}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                  {!apk.is_active && (
                    <button
                      data-testid={`apk-activate-${apk.id}`}
                      onClick={() => activate(apk)}
                      className="inline-flex items-center gap-1.5 bg-[#F4FAF8] hover:bg-[#E1EEEA] border border-[#C9DDD6] text-[#1F4E5F] px-3 py-1.5 rounded-full text-xs font-semibold"
                    >
                      <Star className="w-3.5 h-3.5" /> Make active
                    </button>
                  )}
                  <button
                    data-testid={`apk-delete-${apk.id}`}
                    onClick={() => remove(apk)}
                    className="inline-flex items-center gap-1.5 text-rose-600 hover:bg-rose-50 border border-rose-200 px-3 py-1.5 rounded-full text-xs font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
